import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import Starfield from "@/components/Starfield";
import { IMAGES, TRUST_BADGES } from "@/data/content";
import { openWhatsApp } from "@/lib/api";

const line = {
  hidden: { y: "110%", opacity: 0 },
  show: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 1.15, delay: 0.35 + i * 0.14, ease: [0.8, 0, 0.15, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-void grain"
      data-testid="hero-section"
    >
      {/* Parallax product photo */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0"
        aria-hidden="true"
      >
        <img
          src={IMAGES.heroCar}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          style={{ maskImage: "radial-gradient(ellipse at 65% 55%, black 30%, transparent 78%)", WebkitMaskImage: "radial-gradient(ellipse at 65% 55%, black 30%, transparent 78%)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/70 to-void" />
      </motion.div>
      <Starfield />

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pt-40 md:pt-44 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.9 }}
          className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full glass gradient-border"
          data-testid="hero-eyebrow"
        >
          <span className="w-2 h-2 rounded-full gradient-bg wa-pulse flex-shrink-0 mt-[3px] self-start" />
          <span className="eyebrow !text-[13px] md:!text-sm !tracking-[0.14em] md:!tracking-[0.18em] gradient-text font-bold leading-relaxed">
            India's first buyer-side car price negotiation consultancy
          </span>
        </motion.div>

        <h1 className="mt-6 font-serif tracking-tight text-white text-[13vw] leading-[0.92] md:text-[8.5vw] lg:text-[7.4rem] xl:text-[8.6rem] font-medium" data-testid="hero-headline">
          <span className="masked-line">
            <motion.span variants={line} initial="hidden" animate="show" custom={0} className="block">
              Your Saarthi.
            </motion.span>
          </span>
          <span className="masked-line">
            <motion.span variants={line} initial="hidden" animate="show" custom={1} className="block italic gradient-text">
              Never Theirs.
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 md:mt-10 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.9, ease: [0.8, 0, 0.2, 1] }}
              className="font-serif text-2xl md:text-3xl text-white leading-snug"
              data-testid="hero-usp-line"
            >
              You choose the car. <span className="italic gradient-text">We negotiate the deal.</span>
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.98, duration: 0.9, ease: [0.8, 0, 0.2, 1] }}
              className="mt-4 text-base md:text-lg text-silver/70 max-w-xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              Research. Negotiation. Protection. One advisor on your side — zero dealer commission, guaranteed.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <button
                onClick={() => openWhatsApp({ source: "hero_primary" })}
                className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full text-sm md:text-base font-medium text-white gradient-bg shadow-[0_20px_60px_-15px_rgba(139,92,246,0.6)] hover:shadow-[0_25px_80px_-15px_rgba(244,63,94,0.65)] hover:scale-[1.03] transition-transform duration-300"
                data-testid="hero-whatsapp-btn"
              >
                Talk to your Saarthi
                <span className="w-2 h-2 rounded-full bg-white/90 wa-pulse" />
              </button>
              <a
                href="#tiers"
                className="inline-flex items-center gap-2 text-sm text-silver/70 hover:text-white transition-colors duration-200"
                data-testid="hero-secondary-cta"
              >
                See the tiers <ArrowDown className="w-4 h-4" />
              </a>
            </motion.div>
          </div>

          <div className="md:col-span-5">
            <motion.ul
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.12, delayChildren: 1.3 } } }}
              className="grid grid-cols-3 gap-3 md:gap-4"
              data-testid="hero-trust-badges"
            >
              {TRUST_BADGES.map((b) => (
                <motion.li
                  key={b.k}
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.8 } } }}
                  className="glass rounded-2xl p-4 md:p-5"
                >
                  <div className="font-serif text-2xl md:text-3xl text-white">{b.k}</div>
                  <div className="mt-1 text-[11px] md:text-xs uppercase tracking-wider text-silver/60">{b.v}</div>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </motion.div>

      {/* Bottom marquee-like scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-silver/50"
      >
        <span>Scroll</span>
        <span className="w-8 h-px bg-silver/40" />
      </motion.div>
    </section>
  );
}
